import { useTranslation } from 'react-i18next';

import { useNotifications } from '@/features/notifications/hooks/use-notifications';
import {
  NotificationContentType,
  TrackingRequestSentNotificationMetadata,
} from '@/features/notifications/notification-types';
import { useCreateTrackingRequest } from '@/features/projects/kpi-tracking-entry/hooks/use-create-tracking-request';
import ChatBubbleIcon from '@/shared/assets/bubble.svg?react';
import { Button } from '@/shared/components/ui/button';
import { toast } from '@/shared/hooks/use-toast';

export const RequestTrackingButton = ({
  projectId,
  orgId,
}: {
  projectId: number;
  orgId: number | null;
}) => {
  const { t } = useTranslation('project', { keyPrefix: 'TrackingReports' });
  const { canRequestAgain } = useRequestTimeout(projectId);

  const { mutate: createTrackingRequest } = useCreateTrackingRequest({
    onSuccess: () => {
      toast({
        title: t('TrackingRequests.success'),
      });
    },
  });

  return (
    <Button
      variant={'outline'}
      btnType="secondary"
      onClick={() => {
        if (canRequestAgain() && orgId) {
          createTrackingRequest({
            projectId,
            orgId,
          });
        } else {
          toast({
            title: t('TrackingRequests.timeout_hint'),
          });
        }
      }}
    >
      <ChatBubbleIcon />
      {t('TrackingRequests.request_tracking')}
    </Button>
  );
};

const useRequestTimeout = (projectId: number) => {
  const { data: notifications } = useNotifications();
  const canRequestAgain = () => {
    if (!notifications) return true;

    const trackingRequestNotifications = notifications.filter(
      (notification) => {
        const isTrackingRequestSent =
          notification.content_type ===
          NotificationContentType.tracking_request_sent;
        const metaData =
          notification.metadata as TrackingRequestSentNotificationMetadata;
        const isClientProjectId = metaData.client_project_id === projectId;
        return isTrackingRequestSent && isClientProjectId;
      },
    );

    if (trackingRequestNotifications.length === 0) return true;

    const lastRequest = trackingRequestNotifications.reduce(
      (latest, current) => {
        const latestTime = new Date(latest.created_at ?? '').getTime();
        const currentTime = new Date(current.created_at ?? '').getTime();
        return currentTime > latestTime ? current : latest;
      },
    );

    const lastRequestTime = new Date(lastRequest.created_at ?? '').getTime();
    const twentyFourHoursInMs = 24 * 60 * 60 * 1000;
    const twentyFourHoursAgoTime = Date.now() - twentyFourHoursInMs;

    const result = lastRequestTime < twentyFourHoursAgoTime;

    return result;
  };

  return { canRequestAgain };
};
